/**
 * Deferred megamenu images + color labels — keep initial page Liquid light.
 * Per-handle Section Rendering API (one product/collection at a time) avoids
 * the storefront product hydration limit that breaks a bulk megamenu section.
 */
(() => {
  const IMAGE_SELECTOR = '[data-mega-image]';
  const COLOR_SELECTOR = '[data-mega-color]';
  const FETCH_CONCURRENCY = 6;
  const metaCache = new Map();

  const rootPath = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const sizedUrl = (src, width, height, crop) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      if (height) {
        url.searchParams.set('height', String(height));
        if (crop) url.searchParams.set('crop', crop);
      } else {
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
      }
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  const finishImageEl = (el) => {
    el.classList.remove('is-mega-shimmer');
    el.dataset.megaLoaded = 'true';
  };

  const applyImageToEl = (el, src, alt) => {
    const img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!img || !src) {
      finishImageEl(el);
      return;
    }

    const width = Number(el.dataset.megaWidth) || 300;
    const height = el.dataset.megaHeight ? Number(el.dataset.megaHeight) : null;
    const crop = el.dataset.megaCrop || null;
    const finalSrc = sizedUrl(src, width, height, crop);

    const done = () => finishImageEl(el);
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    if (alt) img.alt = alt;
    img.src = finalSrc;
    if (img.complete) done();
    else if (img.decode) img.decode().then(done).catch(done);
  };

  const applyColorToHandle = (type, handle, colorLabel) => {
    document.querySelectorAll(
      `${COLOR_SELECTOR}[data-mega-type="${type}"][data-mega-handle="${CSS.escape(handle)}"]`
    ).forEach((el) => {
      if (el.dataset.megaLoaded === 'true') return;
      if (!colorLabel) {
        el.remove();
        return;
      }
      el.replaceChildren();
      const circle = document.createElement('span');
      circle.className = 'colorCircle';
      const text = document.createElement('span');
      text.textContent = colorLabel;
      el.append(circle, text);
      el.hidden = false;
      el.dataset.megaLoaded = 'true';
    });
  };

  const parseSectionJson = async (url, jsonId) => {
    if (metaCache.has(url)) return metaCache.get(url);

    const promise = (async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const jsonEl = doc.querySelector(`#${jsonId}`);
      if (!jsonEl) throw new Error(`Missing ${jsonId}`);
      return JSON.parse(jsonEl.textContent);
    })().catch((err) => {
      metaCache.delete(url);
      throw err;
    });

    metaCache.set(url, promise);
    return promise;
  };

  const fetchProductMeta = (handle) => parseSectionJson(
    `${rootPath()}products/${encodeURIComponent(handle)}?section_id=mega-product-meta`,
    'MegaProductMetaJson'
  );

  const fetchCollectionMeta = (handle) => parseSectionJson(
    `${rootPath()}collections/${encodeURIComponent(handle)}?section_id=mega-collection-meta`,
    'MegaCollectionMetaJson'
  );

  const runPool = async (items, worker, concurrency) => {
    const queue = items.slice();
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) || 0 }, async () => {
      while (queue.length) {
        const item = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await worker(item);
      }
    }));
  };

  const collectHandles = (root = document) => {
    const products = new Set();
    const collections = new Set();

    root.querySelectorAll(`${IMAGE_SELECTOR}, ${COLOR_SELECTOR}`).forEach((el) => {
      if (el.dataset.megaLoaded === 'true') return;
      const { megaType: type, megaHandle: handle } = el.dataset;
      if (!type || !handle) return;
      if (type === 'product') products.add(handle);
      if (type === 'collection') collections.add(handle);
    });

    return { products: [...products], collections: [...collections] };
  };

  const loadAndApply = async (root = document) => {
    const { products, collections } = collectHandles(root);
    if (!products.length && !collections.length) return;

    await Promise.all([
      runPool(products, async (handle) => {
        try {
          const meta = await fetchProductMeta(handle);
          root.querySelectorAll(
            `${IMAGE_SELECTOR}[data-mega-type="product"][data-mega-handle="${CSS.escape(handle)}"]`
          ).forEach((el) => {
            if (el.dataset.megaLoaded === 'true') return;
            applyImageToEl(el, meta.src, meta.alt);
          });
          applyColorToHandle('product', handle, meta.colorLabel);
        } catch (e) {
          root.querySelectorAll(
            `${IMAGE_SELECTOR}[data-mega-type="product"][data-mega-handle="${CSS.escape(handle)}"]`
          ).forEach(finishImageEl);
          applyColorToHandle('product', handle, null);
        }
      }, FETCH_CONCURRENCY),

      runPool(collections, async (handle) => {
        try {
          const meta = await fetchCollectionMeta(handle);
          root.querySelectorAll(
            `${IMAGE_SELECTOR}[data-mega-type="collection"][data-mega-handle="${CSS.escape(handle)}"]`
          ).forEach((el) => {
            if (el.dataset.megaLoaded === 'true') return;
            applyImageToEl(el, meta.src, meta.alt);
          });
        } catch (e) {
          root.querySelectorAll(
            `${IMAGE_SELECTOR}[data-mega-type="collection"][data-mega-handle="${CSS.escape(handle)}"]`
          ).forEach(finishImageEl);
        }
      }, FETCH_CONCURRENCY)
    ]);
  };

  const init = () => {
    if (window.Shopify && Shopify.designMode) return;
    if (!document.querySelector(`${IMAGE_SELECTOR}, ${COLOR_SELECTOR}`)) return;

    // Prefetch only the first sidebar/columns panel on idle (keeps request volume sane)
    const prefetch = () => {
      const firstPanel = document.querySelector('.js-mega-nav .disclosure__panel, .js-mega-nav .main-nav__child');
      loadAndApply(firstPanel || document.querySelector('.js-mega-nav') || document);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
      window.addEventListener('load', () => setTimeout(prefetch, 1));
    }

    document.querySelectorAll('.js-mega-nav').forEach((nav) => {
      nav.addEventListener('pointerenter', () => loadAndApply(nav), { passive: true });
    });

    // Sidebar column switches + mobile toggles
    document.querySelector('main-menu')?.addEventListener('pointerenter', (evt) => {
      const panel = evt.target.closest?.('.disclosure__panel, nav-menu, .js-mega-nav');
      if (panel) loadAndApply(panel);
    }, true);

    document.querySelector('main-menu')?.addEventListener('toggle', (evt) => {
      if (evt.target && evt.target.open) loadAndApply(evt.target);
    }, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
